import { useCallback, useState, useEffect } from 'react';
import { Todo } from '../types/data';
import { todoData } from '../data/todoData';
import { todosToSnakeCase, parseStoredTodos } from '../utils/dataTransform';
import { TODO_SYNC_STATE_KEYS, todoLocalRepo } from '../utils/localDb/todoLocalRepo';
import { todoRepo } from '../utils/repos/todoRepo';

const STORAGE_KEY = 'todos';
const BACKEND = (process.env.REACT_APP_BACKEND || 'local').toLowerCase();

/**
 * Todoデータを管理するカスタムフック
 * localStorageでデータを永続化し、初回起動時はtodoDataを使用
 */
export const useTodos = () => {
  // localStorageからデータを読み込み、なければtodoDataを使用（supabase時は初期空 → fetch）
  const [todos, setTodos] = useState<Todo[]>(() => {
    if (BACKEND !== 'supabase') {
      try {
        const storedTodos = localStorage.getItem(STORAGE_KEY);
        if (storedTodos) {
          return parseStoredTodos(storedTodos);
        }
      } catch (error) {
        console.error('Failed to load todos from localStorage:', error);
      }
      return todoData;
    }
    return [];
  });
  const [syncRevision, setSyncRevision] = useState(0);
  const [syncRequest, setSyncRequest] = useState(0);

  const bumpSyncRevision = useCallback((): void => {
    setSyncRevision(prev => prev + 1);
  }, []);

  const refreshFromServer = useCallback(() => {
    if (BACKEND === 'supabase') setSyncRequest(previous => previous + 1);
  }, []);

  // Supabase: 初回ロードと Realtime 通知後の差分同期
  useEffect(() => {
    if (BACKEND !== 'supabase') return;
    let alive = true;
    (async () => {
      try {
        try {
          const cachedRows = await todoLocalRepo.listTodos();
          if (alive && cachedRows.length > 0) {
            bumpSyncRevision();
          }
        } catch (cacheReadError) {
          console.error('Failed to load todos from IndexedDB:', cacheReadError);
        }

        const todosCursor = await todoLocalRepo.getSyncCursor(TODO_SYNC_STATE_KEYS.todosLastSyncedAt);
        if (todosCursor) {
          const rows = await todoRepo.listTodosModifiedAfter(todosCursor);
          try {
            await todoLocalRepo.upsertTodos(rows);
            await todoLocalRepo.setTodoSyncCursorFromRows(rows);
          } catch (cacheError) {
            console.error('Failed to cache todo diffs in IndexedDB:', cacheError);
          }
        } else {
          const rows = await todoRepo.listTodos();
          try {
            await todoLocalRepo.replaceTodos(rows);
            const [todoTagMapRows, todoStockItemRows] = await Promise.all([
              todoRepo.listTodoTagMapRows(),
              todoRepo.listTodoStockItemRows(),
            ]);
            await Promise.all([
              todoLocalRepo.upsertTodoTagMapRows(todoTagMapRows),
              todoLocalRepo.upsertTodoStockItemRows(todoStockItemRows.map(row => ({
                ...row,
                is_checked: !!row.is_checked,
              }))),
            ]);
            await todoLocalRepo.setTodoSyncCursorFromRows(rows);
            await Promise.all([
              todoLocalRepo.setTodoTagMapSyncCursorFromRows(todoTagMapRows),
              todoLocalRepo.setTodoStockItemsSyncCursorFromRows(todoStockItemRows),
            ]);
          } catch (cacheError) {
            console.error('Failed to cache todos in IndexedDB:', cacheError);
          }
        }

        try {
          const [todoTagMapCursor, todoStockItemsCursor] = await Promise.all([
            todoLocalRepo.getSyncCursor(TODO_SYNC_STATE_KEYS.todoTagMapLastSyncedAt),
            todoLocalRepo.getSyncCursor(TODO_SYNC_STATE_KEYS.todoStockItemsLastSyncedAt),
          ]);

          if (todoTagMapCursor || todoStockItemsCursor) {
            const [todoTagMapRows, todoStockItemRows] = await Promise.all([
              todoTagMapCursor ? todoRepo.listTodoTagMapModifiedAfter(todoTagMapCursor) : Promise.resolve([]),
              todoStockItemsCursor ? todoRepo.listTodoStockItemsModifiedAfter(todoStockItemsCursor) : Promise.resolve([]),
            ]);

            await Promise.all([
              todoLocalRepo.upsertTodoTagMapRows(todoTagMapRows),
              todoLocalRepo.upsertTodoStockItemRows(todoStockItemRows.map(row => ({
                ...row,
                is_checked: !!row.is_checked,
              }))),
            ]);
            await Promise.all([
              todoLocalRepo.setTodoTagMapSyncCursorFromRows(todoTagMapRows),
              todoLocalRepo.setTodoStockItemsSyncCursorFromRows(todoStockItemRows),
            ]);
          } else if (todosCursor) {
            const relationMaxModifiedAts = await todoRepo.getTodoRelationMaxModifiedAts();
            await Promise.all([
              todoLocalRepo.setSyncStateFromModifiedAts(
                TODO_SYNC_STATE_KEYS.todoTagMapLastSyncedAt,
                [relationMaxModifiedAts.todoTagMap]
              ),
              todoLocalRepo.setSyncStateFromModifiedAts(
                TODO_SYNC_STATE_KEYS.todoStockItemsLastSyncedAt,
                [relationMaxModifiedAts.todoStockItems]
              ),
            ]);
          }
        } catch (relationCursorError) {
          console.error('Failed to sync todo relation cursors in IndexedDB:', relationCursorError);
        }
        if (!alive) return;
        bumpSyncRevision();
      } catch (e) {
        console.error('Failed to load todos from Supabase:', e);
      }
    })();
    return () => { alive = false; };
  }, [bumpSyncRevision, syncRequest]);

  // todosの変更をlocalStorageに保存（snake_case形式で保存）: localモードのみ
  useEffect(() => {
    if (BACKEND === 'supabase') return;
    try {
      const todoEntities = todosToSnakeCase(todos);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(todoEntities));
    } catch (error) {
      console.error('Failed to save todos to localStorage:', error);
    }
  }, [todos]);

  // 新しいTodoを作成
  const createTodo = async (input: Omit<Todo, 'id' | 'createdAt' | 'modifiedAt'> & { id?: string }): Promise<Todo> => {
    const nowIso = new Date().toISOString();
    const draft: Todo = {
      ...input,
      id: input.id || crypto.randomUUID(),
      deleted: false,
      createdAt: nowIso,
      modifiedAt: nowIso,
    };

    if (BACKEND === 'supabase') {
      try {
        // habitId が UUID でない（例: 'habit-shopping'）場合は DB に送らない
        const isUuid = (v: any) => typeof v === 'string' && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/.test(v);
        const cleaned: Todo = {
          ...draft,
          habitId: isUuid(draft.habitId) ? draft.habitId : undefined,
        };
        const saved = await todoRepo.createTodo(cleaned);
        await todoLocalRepo.upsertTodos([saved]);
        bumpSyncRevision();
        return saved;
      } catch (e) {
        console.error('Failed to create todo on Supabase:', e);
        throw e;
      }
    } else {
      setTodos(prev => [...prev, draft]);
      return draft;
    }
  };

  // Todoを更新
  const updateTodo = async (id: string, updates: Partial<Omit<Todo, 'id' | 'createdAt'>>, original?: Todo): Promise<Todo | undefined> => {
    const modifiedAt = new Date().toISOString();

    if (BACKEND === 'supabase') {
      const before = original ?? await todoLocalRepo.getTodoById(id);
      if (!before) throw new Error('編集元のTodoを取得できませんでした。');
      const saved = await todoRepo.updateTodo(id, updates, before);
      await todoLocalRepo.upsertTodos([saved]);
      bumpSyncRevision();
      return saved;
    }

    setTodos(prevTodos => {
      const index = prevTodos.findIndex(todo => todo.id === id);
      if (index === -1) return prevTodos;

      const updatedTodos = [...prevTodos];
      updatedTodos[index] = {
        ...updatedTodos[index],
        ...updates,
        modifiedAt,
      };
      return updatedTodos;
    });
    return { ...(original ?? todos.find(todo => todo.id === id)!), ...updates, modifiedAt };
  };

  // Todoを論理削除（deleted=trueに設定）
  const deleteTodo = (id: string, original?: Todo): Promise<Todo | undefined> => updateTodo(id, { deleted: true }, original);

  const toggleTodoComplete = async (id: string): Promise<Todo | undefined> => {
    const todo = BACKEND === 'supabase' ? await todoLocalRepo.getTodoById(id) : todos.find(todo => todo.id === id);
    if (!todo) return;
    return updateTodo(id, { done: !todo.done, doneAt: !todo.done ? new Date().toISOString() : undefined }, todo);
  };

  // IDでTodoを取得
  const getTodoById = (id: string): Todo | undefined => {
    return todos.find(todo => todo.id === id);
  };

  // 日付でTodoをフィルタ
  const getTodosByDate = (date: string): Todo[] => {
    return todos.filter(todo => {
      if (!todo.startAt) return false;
      const todoDate = new Date(todo.startAt).toDateString();
      const targetDate = new Date(date).toDateString();
      return todoDate === targetDate;
    });
  };

  // データをtodoDataにリセット
  const resetToDefaultData = (): void => {
    setTodos(todoData);
  };

  // localStorageをクリア
  const clearStorage = (): void => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setTodos(todoData);
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
    }
  };

  return {
    todos,
    syncRevision,
    refreshFromServer,
    createTodo,
    updateTodo,
    deleteTodo,
    toggleTodoComplete,
    getTodoById,
    getTodosByDate,
    resetToDefaultData,
    clearStorage,
  };
};

export default useTodos;
